// src/Pages/Dashboard/widgets/ProjectView/Tabs/Reports/ProjectReportPreview.js

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axiosWithAuth from "../../../../../../Redux/UtilsRedux/axiosWithAuth";
import html2pdf from "html2pdf.js";
import { API_ROUTES } from "../../../../../../Redux/Constants/apiRoutes";
import "../../../../../../Stylesheets/Dashboard/Tabs/Reports/ExpenseReportPrint.css";
import site360Logo from "../../../../../../assets/images/logo.png";
import { FaChevronLeft } from "react-icons/fa";

// Overall project filters modal (you should already have this wired)
import ProjectReportModal from "../../Modal/ProjectReportModal";

const formatCurrency = (val) => {
  if (val == null || Number.isNaN(Number(val))) return "₹0";
  return (
    "₹" + Number(val).toLocaleString("en-IN", { maximumFractionDigits: 2 })
  );
};

const formatDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const formatDateTime = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ProjectReportPreview = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  const qs = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const queryParams = useMemo(
    () => ({
      type: qs.get("type") || "project",
      projectId: qs.get("projectId"),
      from: qs.get("from"),
      to: qs.get("to"),
      detail: qs.get("detail") || "summary", // summary | detailed
    }),
    [qs]
  );

  // ---------- Fetch JSON for project report ----------
  useEffect(() => {
    const fetchReport = async () => {
      try {
        if (!queryParams.projectId || !queryParams.from || !queryParams.to) {
          setError("Missing projectId / from / to");
          setLoading(false);
          return;
        }

        const params = new URLSearchParams({
          type: "project",
          projectId: queryParams.projectId,
          from: queryParams.from,
          to: queryParams.to,
          detail: queryParams.detail,
          format: "json",
        }).toString();

        const url = `${API_ROUTES.GENERATE_REPORT}?${params}`;
        const res = await axiosWithAuth.get(url);

        setReport(res.data.data);
        setError(null);
      } catch (err) {
        console.error("Project report fetch failed", err);
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load project report data";
        setError(msg);
        setReport(null);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [queryParams]);

  // ---------- PDF download ----------
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPdf = async () => {
    if (!queryParams.projectId || !queryParams.from || !queryParams.to) return;

    const container = document.getElementById("project-report-doc");
    if (!container) {
      console.error("Project report container not found");
      return;
    }

    try {
      setDownloading(true);

      const filename = `project-report-${queryParams.from}-to-${queryParams.to}.pdf`;

      const opt = {
        margin: [10, 10, 10, 10],
        filename,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
        },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"] },
      };

      await html2pdf().set(opt).from(container).save();
    } catch (e) {
      console.error("Project PDF download failed", e);
    } finally {
      setDownloading(false);
    }
  };

  // ---------- Navigation back ----------
  const handleBackToProject = () => {
    if (location.state?.returnTo) {
      navigate(location.state.returnTo);
      return;
    }
    navigate(-1);
  };

  // ---------- Filters modal state ----------
  const [showFilters, setShowFilters] = useState(false);
  const [tmpFrom, setTmpFrom] = useState(queryParams.from || "");
  const [tmpTo, setTmpTo] = useState(queryParams.to || "");
  const [tmpDetail, setTmpDetail] = useState(queryParams.detail || "summary");

  useEffect(() => {
    if (!showFilters) return;
    setTmpFrom(queryParams.from || "");
    setTmpTo(queryParams.to || "");
    setTmpDetail(queryParams.detail || "summary");
  }, [showFilters, queryParams]);

  const handleApplyFilters = () => {
    const next = new URLSearchParams(location.search);

    next.set("from", tmpFrom);
    next.set("to", tmpTo);
    next.set("detail", tmpDetail);
    next.set("type", "project");

    setLoading(true);
    setError(null);

    navigate({ search: next.toString() }, { replace: false });

    setShowFilters(false);
  };

  // ---------- Loading / Error states ----------
  if (loading) {
    return (
      <div className="report-print-root">
        <div className="report-preview-scroll">
          <div className="report-print-shell">
            <div className="report-loading">Preparing project report…</div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="report-print-root">
        <div className="report-preview-scroll">
          <div className="report-print-shell">
            <div className="report-error">
              <h2>Unable to generate project report</h2>
              <p>{error || "No data available for the selected filters."}</p>
              <button
                className="report-filter-btn"
                onClick={() => setShowFilters(true)}
              >
                Edit Filters
              </button>
              <button
                className="report-back-btn"
                style={{ marginTop: 8 }}
                onClick={handleBackToProject}
              >
                <FaChevronLeft size={14} />
              </button>
            </div>
          </div>
        </div>

        <ProjectReportModal
          show={showFilters}
          onClose={() => setShowFilters(false)}
          startDate={tmpFrom}
          endDate={tmpTo}
          setStartDate={setTmpFrom}
          setEndDate={setTmpTo}
          format={tmpDetail}
          setFormat={setTmpDetail}
          mode="filters"
          onApply={handleApplyFilters}
          applyLabel="Apply Filters"
        />
      </div>
    );
  }

  // ---------- Unpack project report ----------
  const {
    meta = {},
    summary = {},
    expenses = null,
    materials = null,
    labor = null,
  } = report;

  const expenseSummary = expenses?.summary || {};
  const materialSummary = materials?.summary || {};
  const laborSummary = labor?.summary || {};

  const budget = summary.budget ?? meta.budget ?? 0;

  const totalExpenses = expenseSummary.totalExpenses ?? 0;
  const totalMaterialSpend = materialSummary.totalSpend ?? 0;
  const totalLaborPaid = laborSummary.totalPaidAmount ?? 0;
  const totalLaborOutstanding = laborSummary.totalOutstanding ?? 0;

  const totalCost = totalExpenses + totalMaterialSpend + totalLaborPaid;
  const variance = budget ? budget - totalCost : null;

  return (
    <div className="report-print-root">
      {/* top toolbar */}
      <div className="report-toolbar">
        <div className="report-toolbar-left">
          <button
            className="report-back-btn"
            onClick={handleBackToProject}
            aria-label="Back to Project"
          >
            <FaChevronLeft size={14} />
          </button>

          <div className="report-toolbar-title">
            <h2>Project Report Preview</h2>
            <span className="report-toolbar-period">
              {queryParams.from} → {queryParams.to}
            </span>
          </div>
        </div>

        <div className="report-toolbar-right">
          <button
            className="report-filter-btn"
            onClick={() => setShowFilters(true)}
          >
            Edit Filters
          </button>

          <button
            className="report-download-btn"
            onClick={handleDownloadPdf}
            disabled={downloading}
          >
            {downloading ? "Generating PDF…" : "Download PDF"}
          </button>
        </div>
      </div>

      {/* screen watermark */}
      <div className="report-watermark">Site360</div>

      {/* scrollable PDF region */}
      <div className="report-preview-scroll">
        <div id="project-report-doc" className="report-print-shell">
          {/* watermark inside PDF */}
          <img
            src={site360Logo}
            alt=""
            className="report-doc-watermark"
            crossOrigin="anonymous"
          />

          {/* HEADER */}
          <header className="report-header">
            <div className="report-header-left">
              <div className="report-logo">Site360</div>
              <div className="report-title-block">
                <h1>Project Overview Report</h1>
                {meta.projectName && (
                  <p className="report-project">{meta.projectName}</p>
                )}
                {meta.location && (
                  <p className="report-project-sub">{meta.location}</p>
                )}
              </div>
            </div>

            <div className="report-header-right">
              <div className="report-meta-row">
                <span className="label">Type</span>
                <span className="value">Project</span>
              </div>

              <div className="report-meta-row">
                <span className="label">Period</span>
                <span className="value">
                  {(meta.from || queryParams.from || "—")} →{" "}
                  {(meta.to || queryParams.to || "—")}
                </span>
              </div>

              <div className="report-meta-row">
                <span className="label">Generated</span>
                <span className="value">
                  {formatDateTime(meta.generatedAt)}
                </span>
              </div>
            </div>
          </header>

          {/* SUMMARY */}
          <section className="report-section">
            <div className="section-header">
              <h2>Project Summary</h2>
              <p>High-level snapshot combining Labor, Materials and Expenses.</p>
            </div>

            <div className="metric-grid">
              <div className="metric-card">
                <div className="metric-label">Project Budget</div>
                <div className="metric-value">{formatCurrency(budget)}</div>
              </div>

              <div className="metric-card">
                <div className="metric-label">Expense (Non-Labor)</div>
                <div className="metric-value">
                  {formatCurrency(totalExpenses)}
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-label">Material Spend</div>
                <div className="metric-value">
                  {formatCurrency(totalMaterialSpend)}
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-label">Labor Paid</div>
                <div className="metric-value">
                  {formatCurrency(totalLaborPaid)}
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-label">Labor Outstanding</div>
                <div className="metric-value">
                  {formatCurrency(totalLaborOutstanding)}
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-label">Total Cost (paid)</div>
                <div className="metric-value">
                  {formatCurrency(totalCost)}
                </div>
                {budget ? (
                  <div className="metric-helper">
                    Variance vs Budget: {formatCurrency(variance)}
                  </div>
                ) : null}
              </div>
            </div>
          </section>

          {/* EXPENSE SECTION (compact) */}
          {expenses && (
            <section className="report-section">
              <div className="section-header">
                <h3>Expense Overview</h3>
                <p>
                  Non-labor project expenses logged in the Expenses module for
                  this period.
                </p>
              </div>

              <div className="metric-grid compact">
                <div className="metric-card">
                  <div className="metric-label">Total Expenses</div>
                  <div className="metric-value">
                    {formatCurrency(expenseSummary.totalExpenses)}
                  </div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">Entries</div>
                  <div className="metric-value">
                    {expenseSummary.totalEntries ?? 0}
                  </div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">Pending Bills</div>
                  <div className="metric-value">
                    {expenseSummary.pendingBill ?? 0}
                  </div>
                </div>
              </div>

              {/* Show first table from expenses, if present */}
              {Array.isArray(expenses.tables) && expenses.tables[0] && (
                <div className="report-subtable-wrapper">
                  <h4 className="subsection-title">
                    {expenses.tables[0].title || "Daily Expense Summary"}
                  </h4>
                  <table className="report-table">
                    <thead>
                      <tr>
                        {expenses.tables[0].columns.map((col) => (
                          <th key={col}>{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {expenses.tables[0].rows.map((row, idx) => (
                        <tr key={idx}>
                          {row.map((cell, i) => (
                            <td
                              key={i}
                              className={
                                i === row.length - 1 ? "right" : ""
                              }
                            >
                              {i === 0 ? formatDate(cell) : cell ?? "-"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          {/* MATERIAL SECTION (compact) */}
          {materials && (
            <section className="report-section">
              <div className="section-header">
                <h3>Material Overview</h3>
                <p>Material usage and spend for this project and period.</p>
              </div>

              <div className="metric-grid compact">
                <div className="metric-card">
                  <div className="metric-label">Total Spend</div>
                  <div className="metric-value">
                    {formatCurrency(materialSummary.totalSpend)}
                  </div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">Materials with Activity</div>
                  <div className="metric-value">
                    {materialSummary.totalMaterials ?? 0}
                  </div>
                  <div className="metric-helper">
                    Receives: {materialSummary.totalReceiveEntries ?? 0} · Usage:{" "}
                    {materialSummary.totalUsageEntries ?? 0}
                  </div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">Pending Bills</div>
                  <div className="metric-value">
                    {materialSummary.pendingBills ??
                      materialSummary.pendingBill ??
                      0}
                  </div>
                </div>
              </div>

              {/* First material table */}
              {Array.isArray(materials.tables) && materials.tables[0] && (
                <div className="report-subtable-wrapper">
                  <h4 className="subsection-title">
                    {materials.tables[0].title || "Material Summary"}
                  </h4>
                  <table className="report-table">
                    <thead>
                      <tr>
                        {materials.tables[0].columns.map((col) => (
                          <th key={col}>{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {materials.tables[0].rows.map((row, idx) => (
                        <tr key={idx}>
                          {row.map((cell, i) => (
                            <td
                              key={i}
                              className={
                                i === row.length - 1 || i === 2 ? "right" : ""
                              }
                            >
                              {cell ?? "-"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          {/* LABOR SECTION (compact) */}
          {labor && (
            <section className="report-section">
              <div className="section-header">
                <h3>Labor Overview</h3>
                <p>Attendance and payments for workers on this project.</p>
              </div>

              <div className="metric-grid compact">
                <div className="metric-card">
                  <div className="metric-label">Total Workers</div>
                  <div className="metric-value">
                    {laborSummary.totalWorkers ?? 0}
                  </div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">Present / Absent / Leave</div>
                  <div className="metric-value">
                    {laborSummary.totalPresentDays ?? 0} /{" "}
                    {laborSummary.totalAbsentDays ?? 0} /{" "}
                    {laborSummary.totalLeaveDays ?? 0}
                  </div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">Paid (period)</div>
                  <div className="metric-value">
                    {formatCurrency(laborSummary.totalPaidAmount)}
                  </div>
                  <div className="metric-helper">
                    Outstanding:{" "}
                    {formatCurrency(laborSummary.totalOutstanding)}
                  </div>
                </div>
              </div>

              {/* First labor table */}
              {Array.isArray(labor.tables) && labor.tables[0] && (
                <div className="report-subtable-wrapper">
                  <h4 className="subsection-title">
                    {labor.tables[0].title || "Daily Attendance & Payments"}
                  </h4>
                  <table className="report-table">
                    <thead>
                      <tr>
                        {labor.tables[0].columns.map((col) => (
                          <th key={col}>{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {labor.tables[0].rows.map((row, idx) => (
                        <tr key={idx}>
                          {row.map((cell, i) => (
                            <td
                              key={i}
                              className={
                                i === row.length - 1 ? "right" : ""
                              }
                            >
                              {i === 0 ? formatDate(cell) : cell ?? "-"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          {/* FOOTER */}
          <footer className="report-footer">
            <div className="footer-brand">Site360</div>
            <div className="footer-text">
              Construction Management Platform · https://site360.app
            </div>
          </footer>
        </div>
      </div>

      {/* bottom sticky bar */}
      <div className="report-bottom-sticky">
        <div className="bottom-meta">
          <div className="title">Project Report</div>
          <div className="period">
            {queryParams.from} → {queryParams.to}
          </div>
        </div>

        <button
          className="report-download-btn"
          onClick={handleDownloadPdf}
          disabled={downloading}
        >
          {downloading ? "Generating PDF…" : "Download PDF"}
        </button>
      </div>

      {/* Filters modal */}
      <ProjectReportModal
        show={showFilters}
        onClose={() => setShowFilters(false)}
        startDate={tmpFrom}
        endDate={tmpTo}
        setStartDate={setTmpFrom}
        setEndDate={setTmpTo}
        format={tmpDetail}
        setFormat={setTmpDetail}
        mode="filters"
        onApply={handleApplyFilters}
        applyLabel="Apply Filters"
      />
    </div>
  );
};

export default ProjectReportPreview;
